import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { inr } from "./outstandingUtils";

const STAT = [["total_rows", "Total rows"], ["matched", "Matched"], ["new", "New"], ["updated", "Updated"], ["duplicate", "Duplicates"], ["needs_review", "Needs review"]];

export default function OutstandingUpload({ customerName, onImported }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [p, setP] = useState(null);
  const [cust, setCust] = useState(customerName || "");
  const parse = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (customerName) fd.append("customer_name", customerName);
    try { const { data } = await api.post("/finance-outstanding/parse", fd); setP(data); setCust(customerName || data.customer_name || ""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read the file"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const commit = async () => {
    if (cust.trim().length < 2) return toast.error("Confirm the customer");
    setBusy(true);
    try { const { data } = await api.post(`/finance-outstanding/imports/${p.import_id}/commit`, { customer_name: cust.trim() }); toast.success(`Imported — ${data.updated} updated, ${data.created} added for review`); setP(null); onImported(cust.trim()); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  const s = p?.summary;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="os-upload">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={(e) => parse(e.target.files?.[0])} data-testid="os-upload-input" />
        <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid="os-upload-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Upload outstanding list</Button>
        <span className="text-[11px] text-slate-500">Excel (.xlsx) or CSV, any number of invoices. Columns are recognised automatically.</span>
      </div>
      {p && (
        <div className="space-y-3" data-testid="os-preview">
          <div className="flex flex-wrap items-center gap-2 text-[12px]"><FileSpreadsheet className="h-4 w-4 text-emerald-700" /><b>{p.file_name}</b><span className="text-slate-500">· as of {p.as_of}</span></div>
          <label className="block text-[11px] text-slate-500">Customer (confirm once)
            <Input value={cust} onChange={(e) => setCust(e.target.value)} className="mt-1 h-8 max-w-md text-[13px] font-semibold" data-testid="os-preview-customer" /></label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {STAT.map(([k, l]) => <div key={k} className="rounded border border-slate-200 p-2" data-testid={`os-stat-${k}`}><div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div><div className="text-lg font-semibold tabular-nums">{p[k] ?? 0}</div></div>)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded border border-slate-200 p-2" data-testid="os-stat-total"><div className="text-[9px] uppercase tracking-wider text-slate-500">Total pending</div><div className="text-lg font-semibold">{inr(s.total_pending)}</div></div>
            <div className="rounded border border-rose-200 bg-rose-50/50 p-2" data-testid="os-stat-overdue"><div className="text-[9px] uppercase tracking-wider text-rose-700">Overdue · {s.overdue_count}</div><div className="text-lg font-semibold text-rose-800">{inr(s.overdue)}</div></div>
            <div className="rounded border border-amber-200 bg-amber-50/50 p-2" data-testid="os-stat-due-today"><div className="text-[9px] uppercase tracking-wider text-amber-800">Due today · {s.due_today_count}</div><div className="text-lg font-semibold text-amber-900">{inr(s.due_today)}</div></div>
          </div>
          {p.rows.some((r) => r.issues?.length) && (
            <details className="text-[12px]" data-testid="os-preview-issues"><summary className="cursor-pointer text-slate-600">{p.rows.filter((r) => r.issues?.length).length} row(s) with notes</summary>
              <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5">{p.rows.filter((r) => r.issues?.length).map((r) => <li key={r.row} className="font-mono text-[11px]">Row {r.row} · {r.invoice_number}: {r.issues.join("; ")}</li>)}</ul></details>
          )}
          <div className="flex gap-2"><Button onClick={commit} disabled={busy} data-testid="os-import-btn">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Import &amp; update</Button>
            <Button variant="ghost" onClick={() => setP(null)} data-testid="os-import-cancel">Cancel</Button></div>
        </div>
      )}
    </div>
  );
}
